/**
 * chipSeqHelpers.js
 *
 * ChIP-seq 分析服務（A-B035 Basic / A-B036 Advanced / A-B037 Customized_1 /
 * A-B038 Customized_2）的純函式。
 *
 * Sample Sheet 的版面固定成對：奇數列是 ChIP，緊接著的偶數列是它的 input。
 * 順序是規定死的，使用者不用（也不能）指定哪一列是什麼，所以「第幾列」就是
 * 唯一的真相來源。input 可以不送，那一列留白即可 —— 留白的列必須保留，
 * 不能往上遞補，否則後面的樣本會整個錯行。
 *
 * 「一個樣本」在不同角色眼中定義不同，這裡兩套都要顧到：
 * - 實驗人員與業務：ChIP 和 input 是兩個送樣樣本 → 樣品數量算實際填了名字的列數
 * - 分析人員：ChIP 加上它的 input 才是一個分析樣本 → 分析需求單一組佔兩列
 *
 * 規則寫在這裡而不是 product_line.config.json，理由同 scRnaExperimentHelpers.js：
 * 以品項代號判斷就好，設定檔不用逐筆標注。設定檔若有 isChIPseq 旗標也一併支援。
 */

import { extractAnalysisServiceCode } from './scRnaExperimentHelpers';

/**
 * 走 ChIP-seq 流程的品項 → 分析需求單 3.0 的 pipeline 名稱（對應 B 欄文字，後端照這個勾 A11~A14）。
 * 客製化品項的備註欄（H13 / H14）不在訂單收集，由業務之後在需求單上補。
 */
const CHIPSEQ_PIPELINE_LABELS = {
  'A-B035': 'Basic',
  'A-B036': 'Advanced',
  'A-B037': 'Customized_1',
  'A-B038': 'Customized_2'
};

export const CHIPSEQ_ROW_ROLES = ['chip', 'input'];

export const CHIPSEQ_ROW_ROLE_LABELS = {
  chip: 'ChIP',
  input: 'Input'
};

/** 一個分析樣本固定佔兩列：ChIP 一列、input 一列。 */
export const CHIPSEQ_ROWS_PER_SAMPLE = CHIPSEQ_ROW_ROLES.length;

/** 第 rowIndex（0 起算）列是 ChIP 還是 input，由位置決定。 */
export const getChIPseqRowRole = (rowIndex = 0) => (
  CHIPSEQ_ROW_ROLES[Number(rowIndex) % CHIPSEQ_ROWS_PER_SAMPLE] || 'chip'
);

/** 第 rowIndex（0 起算）列屬於第幾個分析樣本（1 起算）。 */
export const getChIPseqSampleGroupNo = (rowIndex = 0) => (
  Math.floor(Number(rowIndex) / CHIPSEQ_ROWS_PER_SAMPLE) + 1
);

/** input 可以不送，所以只有 ChIP 那一列的 Sample_Name 是必填。 */
export const isChIPseqSampleNameRequired = (rowIndex = 0) => getChIPseqRowRole(rowIndex) === 'chip';

/** 樣品數量只算實際填了 Sample_Name 的列（留白的 input 不算送樣，也不計價）。 */
export const countChIPseqSubmittedSamples = (sampleSheet = []) => (
  (Array.isArray(sampleSheet) ? sampleSheet : [])
    .filter(row => String(row?.sampleName || '').trim())
    .length
);

/**
 * 判斷某個 product line 設定是不是 ChIP-seq 服務。
 * 沒有設定檔物件時（只有品項字串）也能用，因為代號就足以判斷。
 */
export const isChIPseqServiceConfig = (serviceConfig = null, serviceName = '') => {
  if (serviceConfig?.isChIPseq === true) return true;

  const value = serviceConfig?.value || serviceName;
  return Object.prototype.hasOwnProperty.call(
    CHIPSEQ_PIPELINE_LABELS,
    extractAnalysisServiceCode(value)
  );
};

const normalizeServiceCatalog = (serviceCatalog = {}) => {
  if (!serviceCatalog || typeof serviceCatalog !== 'object') {
    return [];
  }

  return Object.values(serviceCatalog).flatMap(category => (
    Array.isArray(category) ? category : []
  ));
};

const findServiceConfigByValue = (serviceCatalog = {}, serviceName = '') => {
  if (!serviceName) return null;

  return normalizeServiceCatalog(serviceCatalog).find(item => item?.value === serviceName) || null;
};

/**
 * 「分析流程」摘要要用的資訊，介面刻意和 getRNAseqPipelineInfo 一致，
 * 這樣 renderAnalysisFlowBlock 不用為了 ChIP-seq 特別分支。
 */
export const getChIPseqPipelineInfo = ({
  serviceItems = [],
  serviceCatalog = {}
} = {}) => {
  const items = Array.isArray(serviceItems) ? serviceItems : [];

  const selectedService = items
    .flatMap(item => (Array.isArray(item?.services) ? item.services : []))
    .find(svc => {
      const serviceName = svc?.service;
      if (!serviceName) return false;

      return isChIPseqServiceConfig(
        findServiceConfigByValue(serviceCatalog, serviceName),
        serviceName
      );
    });

  if (!selectedService?.service) return null;

  const serviceConfig = findServiceConfigByValue(serviceCatalog, selectedService.service);
  const pipelineLabel = CHIPSEQ_PIPELINE_LABELS[extractAnalysisServiceCode(selectedService.service)];
  if (!pipelineLabel) return null;

  return {
    serviceValue: selectedService.service,
    pipelineLabel,
    description2: serviceConfig?.description2 || ''
  };
};

export const hasChIPseqService = ({
  serviceItems = [],
  serviceCatalog = {}
} = {}) => Boolean(getChIPseqPipelineInfo({ serviceItems, serviceCatalog }));

export const CHIPSEQ_SAMPLE_SHEET_HINTS = [
  '本表兩列為一組：上面那列填 ChIP 的檔案名稱，下面那列填它對應的 input，順序是固定的，不用另外指定。',
  'input 為選填。沒有 input 的樣本請把該列留白，不要往上遞補，否則後面的樣本會整個錯行。',
  '每一列左邊都會標出它屬於第幾個分析樣本、是 ChIP 還是 Input，填的時候可以對照。',
  '「樣品數量」只算實際填了 Sample_Name 的列，留白的 input 不會被算進去。',
  '樣本來源只有沿用以前 PO 的樣本才要填，填那張 PO 的單號；這次新送的樣本留空。'
];

/**
 * 「樣本來源」：一張單可能混用以前 PO 送過的樣本，這一欄記錄該列樣本來自哪張 PO。
 * ChIP 與它的 input 可能來自不同 PO，所以每一列各自填；Sample_Name 留白的列沒有樣本，
 * 來源也一併清空。長度上限與 DB 欄位 sample_source VARCHAR(64) 一致。
 */
export const CHIPSEQ_SAMPLE_SOURCE_MAX_LENGTH = 64;

export const sanitizeChIPseqSampleSource = (value = '') => (
  // 控制字元（貼上時夾帶的換行、tab）換成空白，再把連續空白收成一個。
  Array.from(String(value ?? ''), char => (char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127 ? ' ' : char))
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, CHIPSEQ_SAMPLE_SOURCE_MAX_LENGTH)
    .trim()
);

/**
 * 送出前整理一列的 sampleSource：ChIP-seq 單才保留（沒有 Sample_Name 的列清空），
 * 其他單直接拿掉這個 key，換品項後不會殘留。
 */
export const applyChIPseqSampleSource = (row = {}, isChIPseq = false) => {
  const { sampleSource, ...rest } = row && typeof row === 'object' ? row : {};
  if (!isChIPseq) return rest;

  return {
    ...rest,
    sampleSource: String(rest.sampleName || '').trim() ? sanitizeChIPseqSampleSource(sampleSource) : ''
  };
};

/** 只有 Human / Mouse 有 blacklist，其他物種的 pipeline 會略過這一步。 */
export const CHIPSEQ_BLACKLIST_SPECIES = ['Human', 'Mouse'];

export const CHIPSEQ_SPECIES_NOTICE =
  `只有 ${CHIPSEQ_BLACKLIST_SPECIES.join(' 與 ')} 有 blacklist filtering；`
  + '其他物種的分析流程會略過這個步驟。';

export const hasChIPseqBlacklistFiltering = (speciesCommonName = '') => (
  CHIPSEQ_BLACKLIST_SPECIES.includes(String(speciesCommonName || '').trim())
);
